import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';

/**
 * Owner-side access invite endpoints — canonical member-facing routes
 * (`/public/me/access-invites*`). Responses use the `{ success, data }`
 * envelope. Invite rows are the raw `AccessInvite` columns.
 */

export interface AccessInvite {
  access_invite_id: string;
  reference?: string;
  location_id?: string | null;
  access_entry_type_id?: string | null;
  member_contact_id?: string | null;
  visitor_name?: string;
  visitor_phone?: string;
  visitor_email?: string;
  status: string;
  /** Visit window — backend columns `valid_from` / `valid_until`. */
  valid_from?: string | null;
  valid_until?: string | null;
  credential_kind?: string;
  /** Snapshot of entry-type gate auth methods (pin, otp, qr, …). */
  resolved_credential_methods?: string[] | null;
  accepted_at?: string | null;
  require_visitor_selfie?: boolean;
  biometric_enrolment_status?: string | null;
  visitor_selfie_storage_key?: string | null;
}

export interface AccessInviteComposerSettings {
  default_validity_days: number;
  max_validity_days: number;
}

export interface NewAccessInvitePayload {
  location_id?: string | null;
  access_entry_type_id?: string | null;
  member_contact_id?: string | null;
  visitor_name?: string | null;
  visitor_phone?: string | null;
  visitor_email?: string | null;
  vehicle_reg?: string | null;
  total_adults?: number | null;
  total_minors?: number | null;
  require_visitor_selfie?: boolean;
  pin_lifecycle?: 'one_time' | 'multi_entry';
  valid_until?: string | null;
  valid_from?: string | null;
  public_base_url?: string | null;
}

/** One capturable visitor field's resolved state (channel ceiling ∩ entry-type opt-in). */
export interface CaptureFieldRequirement {
  capture: boolean;
  required: boolean;
}

export interface InviteCaptureRequirements {
  email: CaptureFieldRequirement;
  phone: CaptureFieldRequirement;
  vehicle_reg: CaptureFieldRequirement;
  headcount: CaptureFieldRequirement;
}

export interface EligibleLocation {
  location_id: string;
  name?: string;
  label?: string;
  location_code?: string | null;
  address?: string | null;
}

export interface MemberAccessEntryTypeOption {
  access_entry_type_id: string;
  code?: string | null;
  name: string;
  /** Lucide icon class from tenant settings (e.g. HardHat). */
  portal_icon_class?: string | null;
  credential_combination_mode?: string | null;
  invite_message_template_id?: string | null;
  require_visitor_selfie_default?: boolean;
  notify_supports_email?: boolean;
  notify_supports_sms?: boolean;
  gate_notify_supports_sms?: boolean;
  default_validity_days?: number;
  max_validity_days?: number;
  /** When true, visitor may use the gate multiple times during the validity window. */
  allow_multiple_entries?: boolean;
  /** Which visitor fields this entry type captures/requires (channel ceiling ∩ entry-type opt-in). */
  capture_requirements?: InviteCaptureRequirements;
}

export interface MemberInviteCredentials {
  reference: string;
  credential_methods: string[];
  pin?: string;
  pin_available: boolean;
  qr_payload?: string | null;
  issued_credentials?: Array<{ method: string; qr_payload?: string | null }>;
  cancelled: boolean;
}

export async function fetchAccessInvites(): Promise<AccessInvite[]> {
  const { data } = await tenantApi.get('/public/me/access-invites');
  return unwrapList<AccessInvite>(data);
}

export async function fetchAccessInviteLocations(): Promise<EligibleLocation[]> {
  const { data } = await tenantApi.get('/public/me/access-invite-locations');
  return unwrapList<EligibleLocation>(data);
}

export async function fetchAccessEntryTypesForInvite(): Promise<MemberAccessEntryTypeOption[]> {
  const { data } = await tenantApi.get('/public/me/access-entry-types');
  return unwrapList<MemberAccessEntryTypeOption>(data);
}

export async function fetchAccessInviteSettings(): Promise<AccessInviteComposerSettings> {
  const { data } = await tenantApi.get('/public/me/access-invite-settings');
  return unwrap<AccessInviteComposerSettings>(data);
}

export async function createAccessInvite(
  payload: NewAccessInvitePayload,
): Promise<{ invite: AccessInvite; credentials?: MemberInviteCredentials }> {
  const { data } = await tenantApi.post('/public/me/access-invites', payload, {
    // OpenItem case create + credential issue can exceed the default 20s client timeout.
    timeout: 60_000,
  });
  const body = data as {
    data?: AccessInvite;
    credentials?: MemberInviteCredentials;
  };
  const invite = unwrap<AccessInvite>(data);
  const credentials =
    body && typeof body === 'object' && body.credentials ? body.credentials : undefined;
  return { invite, credentials };
}

export async function fetchMemberInviteCredentials(
  accessInviteId: string,
): Promise<MemberInviteCredentials> {
  const { data } = await tenantApi.get(
    `/public/me/access-invites/${accessInviteId}/credentials`,
    { timeout: 45_000 },
  );
  return unwrap<MemberInviteCredentials>(data);
}

export async function cancelAccessInvite(accessInviteId: string): Promise<void> {
  await tenantApi.post(`/public/me/access-invites/${accessInviteId}/cancel`);
}

export async function resendAccessInviteNotification(
  accessInviteId: string,
  publicBaseUrl?: string,
): Promise<void> {
  await tenantApi.post(`/public/me/access-invites/${accessInviteId}/resend-notification`, {
    ...(publicBaseUrl ? { public_base_url: publicBaseUrl } : {}),
  });
}

export interface MemberRecentPin {
  case_id: string;
  visitor_name: string | null;
  pin: string | null;
  valid_from: string | null;
  valid_until: string | null;
  symptom: string | null;
  linked_access_invite_id: string | null;
}

export async function fetchMemberRecentPins(locationId: string): Promise<MemberRecentPin[]> {
  const { data } = await tenantApi.get('/public/me/access-pins', {
    params: { location_id: locationId },
    timeout: 45_000,
  });
  return unwrapList<MemberRecentPin>(data);
}

export async function cancelMemberPinCase(
  caseId: string,
  locationId: string,
  reason?: string,
): Promise<void> {
  await tenantApi.post(`/public/me/access-pins/${encodeURIComponent(caseId)}/cancel`, {
    location_id: locationId,
    ...(reason ? { reason } : {}),
  });
}

export async function pulseMemberGate(locationId: string): Promise<void> {
  await tenantApi.post('/public/me/access-gate/pulse', { location_id: locationId });
}

export interface AccessGateEvent {
  access_gate_event_id: string;
  message_type: string;
  message: string;
  file_url?: string | null;
  thumbnail_url?: string | null;
  is_read: boolean;
  created_at: string;
  access_invite_id?: string | null;
  visitor_name?: string | null;
}

export async function fetchAccessGateEvents(limit = 50): Promise<AccessGateEvent[]> {
  const { data } = await tenantApi.get('/public/me/access-gate-events', { params: { limit } });
  return unwrapList<AccessGateEvent>(data);
}

export async function markAccessGateEventRead(eventId: string): Promise<void> {
  await tenantApi.put(`/public/me/access-gate-events/${eventId}/read`, {});
}

export async function uploadVisitorSelfie(
  inviteId: string,
  file: File,
  opts?: { public_base_url?: string },
): Promise<AccessInvite> {
  const fd = new FormData();
  fd.append('selfie', file);
  if (opts?.public_base_url) {
    fd.append('public_base_url', opts.public_base_url);
  }
  const { data } = await tenantApi.post(`/public/me/access-invites/${inviteId}/visitor-selfie`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap<AccessInvite>(data);
}
